/**
 * The Main Menu — the first surface after sign-in and consent.
 *
 * The ways in, in the game's own "Static & Shadow" dress and over the same ambient bed as the
 * landing page (the shell keeps the ambient bus live on this route):
 *   - Continue — resume the live autosave (literally where you left off; a fresh account starts new).
 *   - New Game — discard the live autosave and start a fresh night (confirmed if a run is going).
 *   - Load Save — the four manual slots; load one into the game. Saving is done from in-game only.
 *   - Settings — the same audio/accessibility settings the receiver exposes, editable before play.
 *   - Log Out — end the session and return to the landing page.
 *
 * Continue, New Game and Load all end on `/play`; the app shell rebuilds the receiver from whatever
 * save is live at that point. Settings go through the same debounced `sync` the game uses, and the
 * slot list is the shared `saveSlots` component in its load/delete-only `'menu'` mode.
 */
import { h } from '../dom.js';
import { t } from '../../i18n/index.js';
import { brandLogo, footer } from '../components/chrome.js';
import { refreshOverlay } from '../effects/overlay.js';
import { createSaveSlots } from '../components/saveSlots.js';
import type { MeResponse } from '../../api/types.js';
import { isRunInProgress, type EffectsIntensity, type ProgressSync } from '../../state/progress.js';
import type { Mixer } from '../../audio/mixer.js';
import type { View } from '../view.js';

export interface MenuContext {
  me: MeResponse;
  sync: ProgressSync;
  /** Live volume/effects preview; null only if the audio stack never came up. */
  mixer: Mixer | null;
  /** Resume the live autosave. */
  onContinue: () => void;
  /** Discard the live autosave, reset to a fresh night, and enter `/play`. */
  onNewGame: () => void;
  /** Load a slot into the active game and enter it. The app handles adopt + rebuild + navigate. */
  onLoadSlot: (slot: number) => Promise<void>;
  /** End the session and return to the landing page. */
  onLogOut: () => void;
}

export function menuView({
  me,
  sync,
  mixer,
  onContinue,
  onNewGame,
  onLoadSlot,
  onLogOut,
}: MenuContext): View {
  type Screen = 'root' | 'saves' | 'settings';

  const rootScreen = buildRoot();
  const savesScreen = buildSaves();
  const settingsScreen = buildSettings();

  const show = (screen: Screen): void => {
    rootScreen.hidden = screen !== 'root';
    savesScreen.element.hidden = screen !== 'saves';
    settingsScreen.hidden = screen !== 'settings';
    if (screen === 'saves') savesScreen.refresh();
  };

  /* ---- root: Continue / Load Save / Settings -------------------------------------------- */

  function buildRoot(): HTMLElement {
    const action = (
      label: string,
      hint: string,
      primary: boolean,
      onClick: () => void,
    ): HTMLElement =>
      h(
        'button',
        {
          class: `nb-menu__action${primary ? ' nb-menu__action--primary' : ''}`,
          type: 'button',
          onclick: onClick,
        },
        h('span', { class: 'nb-menu__action-label', text: label }),
        h('span', { class: 'nb-menu__action-hint', text: hint }),
      );

    const root = h(
      'section',
      { class: 'nb-menu__screen', 'aria-label': t('menu.title') },
      action(t('menu.continue'), t('menu.continueHint'), true, onContinue),
      action(t('menu.newGame'), t('menu.newGameHint'), false, () => {
        // Only warn when there is a run to lose: a fresh account (autosave equal to a clean
        // default) enters straight in, and an in-progress night takes a two-tap confirm first.
        if (isRunInProgress(sync.progress.get())) confirmNewGame.show();
        else onNewGame();
      }),
      action(t('menu.load'), t('menu.loadHint'), false, () => show('saves')),
      action(t('menu.settings'), t('menu.settingsHint'), false, () => show('settings')),
      action(t('menu.logOut'), t('menu.logOutHint'), false, onLogOut),
    );

    // The inline confirm for New Game lives at the foot of the root screen, hidden until asked for,
    // so starting fresh over an existing run always takes a deliberate second tap.
    const confirmNewGame = inlineConfirm(t('menu.confirmNewGame'), onNewGame);
    root.appendChild(confirmNewGame.element);

    return root;
  }

  /** A hidden two-tap confirm bar, revealed by `.show()`, matching the slot confirm styling. */
  function inlineConfirm(
    question: string,
    onYes: () => void,
  ): { element: HTMLElement; show: () => void } {
    const bar = h(
      'div',
      { class: 'nb-slot__confirm', role: 'alertdialog', 'aria-label': question, hidden: true },
      h('span', { class: 'nb-slot__confirm-q', text: question }),
      h('button', {
        class: 'nb-button nb-button--alert nb-button--small',
        type: 'button',
        text: t('menu.slot.confirmYes'),
        onclick: () => {
          bar.hidden = true;
          onYes();
        },
      }),
      h('button', {
        class: 'nb-button nb-button--quiet nb-button--small',
        type: 'button',
        text: t('menu.slot.confirmNo'),
        onclick: () => {
          bar.hidden = true;
        },
      }),
    );
    return {
      element: bar,
      show: () => {
        bar.hidden = false;
      },
    };
  }

  /* ---- saves: the four slots (load / delete only) --------------------------------------- */

  function buildSaves(): { element: HTMLElement; refresh: () => void } {
    // Load/delete only from the menu — writing a save is an in-game action now.
    const slots = createSaveSlots({ mode: 'menu', sync, onLoadSlot });

    const element = h(
      'section',
      { class: 'nb-menu__screen', 'aria-label': t('menu.saves.title'), hidden: true },
      backBar(t('menu.saves.title'), t('menu.saves.lede')),
      slots.element,
    );

    return { element, refresh: slots.refresh };
  }

  /* ---- settings ------------------------------------------------------------------------- */

  function buildSettings(): HTMLElement {
    const s = () => sync.progress.get().settings;

    const volume = slider('menu-volume', t('radio.volume'), s().masterVolume, (value) => {
      mixer?.setVolume(value);
      sync.patch((c) => ({ ...c, settings: { ...c.settings, masterVolume: value } }));
    });

    const effects = h(
      'select',
      { class: 'nb-select', id: 'menu-effects', 'aria-label': t('radio.effects') },
      ...(['full', 'reduced', 'off'] as const).map((value) =>
        h('option', { value, text: t(`radio.effects.${value}`), selected: s().effects === value }),
      ),
    ) as HTMLSelectElement;
    effects.addEventListener('change', () => {
      const value = effects.value as EffectsIntensity;
      document.documentElement.dataset.nbEffects = value;
      refreshOverlay();
      sync.patch((c) => ({ ...c, settings: { ...c.settings, effects: value } }));
    });

    const ghosts = toggle(
      'menu-ghosts',
      s().muteGhosts,
      t('radio.ghosts.off'),
      t('radio.ghosts.on'),
      (muted) => {
        sync.patch((c) => ({ ...c, settings: { ...c.settings, muteGhosts: muted } }));
      },
    );

    const captions = toggle(
      'menu-captions',
      s().captions,
      t('radio.captions.on'),
      t('radio.captions.off'),
      (on) => {
        sync.patch((c) => ({ ...c, settings: { ...c.settings, captions: on } }));
      },
    );

    const clarity = slider('menu-clarity', t('radio.clarity'), s().voiceClarity, (value) => {
      sync.patch((c) => ({ ...c, settings: { ...c.settings, voiceClarity: value } }));
    });

    const atmospherics = slider(
      'menu-atmospherics',
      t('radio.atmospherics'),
      s().atmospherics,
      (value) => {
        sync.patch((c) => ({ ...c, settings: { ...c.settings, atmospherics: value } }));
      },
    );

    return h(
      'section',
      { class: 'nb-menu__screen', 'aria-label': t('menu.settings.title'), hidden: true },
      backBar(t('menu.settings.title'), t('menu.settings.lede')),
      h(
        'div',
        { class: 'nb-menu__settings' },
        control(t('radio.volume'), 'menu-volume', volume),
        control(t('radio.effects'), 'menu-effects', effects),
        control(t('radio.ghosts'), 'menu-ghosts', ghosts),
        control(t('radio.captions'), 'menu-captions', captions),
        control(t('radio.clarity'), 'menu-clarity', clarity),
        control(t('radio.atmospherics'), 'menu-atmospherics', atmospherics),
      ),
    );
  }

  /* ---- small builders ------------------------------------------------------------------- */

  function backBar(title: string, lede: string): HTMLElement {
    return h(
      'header',
      { class: 'nb-menu__subhead' },
      h('button', {
        class: 'nb-button nb-button--quiet nb-button--small',
        type: 'button',
        text: `‹ ${t('menu.back')}`,
        onclick: () => show('root'),
      }),
      h(
        'div',
        { class: 'nb-menu__subtitles' },
        h('h2', { class: 'nb-menu__subtitle', text: title }),
        h('p', { class: 'nb-lede', text: lede }),
      ),
    );
  }

  function slider(
    id: string,
    label: string,
    value: number,
    onChange: (value: number) => void,
  ): HTMLInputElement {
    const input = h('input', {
      class: 'nb-slider',
      id,
      type: 'range',
      min: '0',
      max: '100',
      step: '1',
      value: String(Math.round(value * 100)),
      'aria-label': label,
    }) as HTMLInputElement;
    input.addEventListener('input', () => onChange(Number(input.value) / 100));
    return input;
  }

  function toggle(
    id: string,
    initial: boolean,
    onText: string,
    offText: string,
    onChange: (value: boolean) => void,
  ): HTMLButtonElement {
    // `initial` is the stored boolean; the button shows the *state*, matching the receiver's toggles.
    const button = h('button', {
      class: 'nb-button nb-button--quiet nb-button--small',
      type: 'button',
      id,
      'aria-pressed': String(initial),
      text: initial ? onText : offText,
    }) as HTMLButtonElement;
    button.addEventListener('click', () => {
      const next = button.getAttribute('aria-pressed') !== 'true';
      button.setAttribute('aria-pressed', String(next));
      button.textContent = next ? onText : offText;
      onChange(next);
    });
    return button;
  }

  const element = h(
    'div',
    { class: 'nb-page nb-page--menu' },
    h(
      'header',
      { class: 'nb-bar' },
      h('span', { class: 'nb-bar__mark' }, brandLogo('nb-bar__logo', t('app.name'))),
      h('span', { class: 'nb-bar__spacer' }),
      h('span', { class: 'nb-bar__account', text: me.account?.email ?? '' }),
    ),
    h(
      'main',
      { class: 'nb-menu', id: 'main' },
      h(
        'div',
        { class: 'nb-menu__hero' },
        h('h1', { class: 'nb-menu__title' }, brandLogo('nb-menu__logo', t('app.name'))),
        h('p', { class: 'nb-menu__lede', text: t('menu.subtitle') }),
      ),
      rootScreen,
      savesScreen.element,
      settingsScreen,
    ),
    footer(),
  );

  return { element };
}

function control(label: string, forId: string, input: HTMLElement): HTMLElement {
  return h(
    'div',
    { class: 'nb-control' },
    h('label', { class: 'nb-control__label', for: forId, text: label }),
    input,
  );
}
